import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) de cada función del menú Ventas. */
export const ventasSections = [
  functionDoc({
    id: 'ventas-clientes-list',
    name: 'Clientes — listado',
    type: 'list',
    route: '/ventas/clients',
    locationPath: 'Ventas > Configuración > Clientes',
    generalDescription:
      'Consulta y mantenimiento del maestro de clientes de ventas. Desde aquí busca receptores por documento o nombre, abre el detalle para editar y crea clientes nuevos que luego aparecerán en cotizaciones, órdenes de servicio de venta y comprobantes. Los usuarios limitados solo ven clientes asociados a su alcance de punto de venta cuando aplique.',
    prerequisites: [
      'Módulo Ventas habilitado en Administrador → Habilitación de módulos.',
      'Usuario con acceso a Ventas (administrador o limitado con permiso de ventas).',
      'Punto de venta seleccionado en la barra superior si su empresa opera con varios POS.',
    ],
    interfaceElements: [
      {
        name: 'Filtros de búsqueda',
        controlType: 'Campos de texto / select',
        required: false,
        editable: true,
        automatic: false,
        description: 'Permite acotar la tabla por nombre, tipo o número de documento.',
        behavior: 'La búsqueda se aplica al listado sin recargar toda la página en la mayoría de casos.',
        example: 'RUC 20123456789',
      },
      {
        name: 'Tabla de clientes',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Muestra identificación, razón social y datos de contacto resumidos.',
        behavior: 'Al hacer clic en una fila o en Editar abre el formulario del cliente.',
      },
      {
        name: 'Botón Nuevo / Crear',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Abre el formulario vacío de registro de cliente.',
        behavior: 'Usuarios limitados en solo lectura no verán acciones de creación.',
      },
    ],
    procedure: [
      'Abra el menú lateral Ventas y expanda Configuración si está cerrado.',
      'Seleccione Clientes.',
      'Opcional: escriba en los filtros para localizar un cliente.',
      'Para modificar, pulse Editar o la fila correspondiente.',
      'Para alta, pulse Nuevo o Crear cliente.',
    ],
    practicalExample: {
      context: 'El cajero debe facturar a una empresa nueva del sector minero.',
      data: {
        Acción: 'Crear cliente antes de la primera venta',
        Documento: 'RUC 20601234567',
        Nombre: 'SERVICIOS MINEROS SAC',
      },
      steps: [
        'Entre a Clientes y pulse Nuevo.',
        'Complete RUC y razón social, guarde.',
        'Vuelva a Registrar venta y busque el cliente en el selector.',
      ],
    },
    validations: [
      'El listado puede estar vacío si aún no hay clientes; no es un error del sistema.',
      'Documentos duplicados se rechazan al guardar en el formulario, no en el listado.',
    ],
    expectedResult: [
      'Ve la tabla actualizada de clientes activos del módulo Ventas.',
      'Puede navegar al formulario de creación o edición sin perder el contexto del POS.',
    ],
    commonErrors: [
      {
        error: 'No aparece el menú Ventas',
        cause: 'Módulo desactivado o usuario contador / sin permiso.',
        solution: 'Solicite al administrador activar Ventas y asignar punto de venta al usuario limitado.',
      },
      {
        error: 'Listado vacío pero el cliente existe en Compras',
        cause: 'El maestro de clientes de ventas es independiente del de proveedores en muchos despliegues.',
        solution: 'Registre el cliente en Ventas o créelo desde el modal rápido al facturar.',
      },
    ],
    relatedFunctions: ['ventas-clientes-form', 'ventas-orders-form'],
    observations: [
      ROLES.limitado,
      'También puede crear clientes desde el botón «Nuevo cliente» dentro de Registrar venta.',
    ],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-clientes-form',
    name: 'Clientes — registro y edición',
    type: 'form',
    route: '/ventas/clients/create',
    locationPath: 'Ventas > Configuración > Clientes > Nuevo / Editar',
    generalDescription:
      'Formulario para dar de alta o actualizar un cliente de ventas: tipo y número de documento, nombre o razón social, dirección y contacto. Estos datos alimentan cotizaciones, OSV, PDF de comprobantes y el receptor en facturación electrónica genérica (Perú). Un cliente bien cargado evita rechazos al emitir facturas con datos incompletos del receptor.',
    prerequisites: [
      'Acceso de escritura a Ventas (no usuario limitado en solo lectura).',
      'Catálogo de tipos de documento disponible en el sistema.',
    ],
    interfaceElements: [
      {
        name: 'Tipo de documento',
        controlType: 'Select',
        required: true,
        editable: true,
        automatic: false,
        description: 'RUC para empresas, DNI u otros según catálogo.',
        behavior: 'Debe ser coherente con la longitud del número ingresado.',
        example: 'RUC',
      },
      {
        name: 'Número de documento',
        controlType: 'Texto',
        required: true,
        editable: true,
        automatic: false,
        description: 'Identificador único del cliente en ventas.',
        behavior: 'No puede repetirse la combinación tipo + número con otro cliente.',
        example: '20123456789',
      },
      {
        name: 'Nombre / razón social',
        controlType: 'Texto',
        required: true,
        editable: true,
        automatic: false,
        description: 'Texto impreso en comprobantes y cotizaciones.',
        behavior: 'Obligatorio para guardar.',
      },
      {
        name: 'Dirección y contacto',
        controlType: 'Texto / área',
        required: false,
        editable: true,
        automatic: false,
        description: 'Útil para entregas y para completar datos del receptor en factura.',
        behavior: 'Recomendable en facturas a empresas.',
      },
      {
        name: 'Guardar',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Persiste el cliente y regresa al listado o cierra el modal.',
        behavior: 'Muestra mensaje de éxito o errores de validación en pantalla.',
      },
    ],
    procedure: [
      'Desde el listado de clientes pulse Nuevo o abra un cliente existente.',
      'Seleccione el tipo de documento.',
      'Ingrese el número sin guiones salvo que el formulario lo normalice.',
      'Complete nombre o razón social y, si aplica, dirección y teléfono.',
      'Pulse Guardar y confirme el mensaje de éxito.',
      'Verifique que el cliente aparece al buscarlo en una cotización o venta.',
    ],
    practicalExample: {
      context: 'Actualización de razón social tras fusión societaria.',
      data: {
        Cliente: 'RUC 20100099999',
        Cambio: 'Nueva razón social en SUNAT',
      },
      steps: [
        'Busque el cliente en el listado y edite.',
        'Modifique solo el nombre/razón social y guarde.',
        'Emita la siguiente factura y revise el PDF.',
      ],
    },
    validations: [
      'Tipo y número de documento obligatorios.',
      'Nombre o razón social obligatorio.',
      'Combinación tipo + número única en el maestro de clientes de ventas.',
    ],
    expectedResult: [
      'Cliente guardado y disponible en selectores de Ventas.',
      'Al editar, los documentos ya emitidos conservan los datos históricos del comprobante.',
    ],
    commonErrors: [
      {
        error: '«El documento ya está registrado»',
        cause: 'Otro cliente usa el mismo tipo y número.',
        solution: 'Busque el cliente existente o corrija el número digitado.',
      },
      {
        error: 'Botón Guardar no responde',
        cause: 'Campos obligatorios vacíos o sesión expirada.',
        solution: 'Revise mensajes en rojo bajo los campos; vuelva a iniciar sesión si hubo 401.',
      },
    ],
    relatedFunctions: ['ventas-clientes-list', 'ventas-orders-form'],
    observations: [
      'Para factura electrónica en producción, valide datos del receptor con su proveedor PSE/OSE; este manual no detalla reglas SUNAT específicas.',
    ],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-lista-precios',
    name: 'Lista de precios por punto de venta',
    type: 'form',
    route: '/ventas/price-list',
    locationPath: 'Ventas > Configuración > Lista de precios',
    generalDescription:
      'Define el precio de venta de cada producto y servicio para el punto de venta activo. Sin precio configurado, al agregar ítems en cotización o venta el sistema puede bloquear la línea o exigir precio manual según configuración. Los servicios de comida en concesionaria toman estos precios al valorar el cuadro de venta producción.',
    prerequisites: [
      'Productos y/o servicios dados de alta en Gestión.',
      'Punto de venta seleccionado.',
      'Permiso de edición en Ventas.',
    ],
    interfaceElements: [
      {
        name: 'Selector de punto de venta (contexto)',
        controlType: 'Barra superior / contexto',
        required: true,
        editable: true,
        automatic: false,
        description: 'Todos los precios son por POS; cambiar de POS muestra otra lista.',
        behavior: 'Usuarios limitados solo ven POS asignados.',
      },
      {
        name: 'Buscador de ítem',
        controlType: 'Texto',
        required: false,
        editable: true,
        automatic: false,
        description: 'Filtra por código SKU o nombre.',
        behavior: 'Reduce filas visibles en la grilla.',
      },
      {
        name: 'Precio unitario',
        controlType: 'Numérico',
        required: true,
        editable: true,
        automatic: false,
        description: 'Monto de lista para el POS; en venta se interpreta según flag de IGV.',
        behavior: 'Al registrar venta los precios de línea se ingresan sin IGV aunque la lista indique «incluye IGV».',
      },
      {
        name: 'Incluye IGV / afecto IGV',
        controlType: 'Checkbox o toggle',
        required: true,
        editable: true,
        automatic: false,
        description: 'Indica si el precio de lista ya contiene IGV o si el ítem lleva impuesto.',
        behavior: 'Recalcula base e IGV al traer precio a cotización o venta.',
      },
      {
        name: 'Moneda',
        controlType: 'Select',
        required: false,
        editable: true,
        automatic: false,
        description: 'PEN o USD si la empresa opera ventas en dólares.',
        behavior: 'Ventas en USD usan tipo de cambio del día de emisión cuando aplica dual currency.',
      },
    ],
    procedure: [
      'Confirme el punto de venta correcto en la barra superior.',
      'Menú Ventas → Configuración → Lista de precios.',
      'Busque el producto o servicio a tarifar.',
      'Ingrese o ajuste precio, moneda y flag de IGV.',
      'Guarde la fila o use guardado masivo si la pantalla lo ofrece.',
      'Pruebe agregando el ítem en una cotización de prueba.',
    ],
    practicalExample: {
      context: 'Apertura de nuevo POS en campamento.',
      data: {
        POS: 'Campamento Norte',
        Producto: 'Agua 625 ml',
        Precio: 'S/ 2.50 con IGV',
      },
      steps: [
        'Seleccione el POS Campamento Norte.',
        'Localice el producto y asigne precio 2.50 marcando incluye IGV.',
        'Guarde y registre una venta de prueba en boleta.',
      ],
    },
    validations: [
      'Precio numérico mayor o igual a cero.',
      'Producto o servicio debe existir en catálogo global.',
    ],
    expectedResult: [
      'El ítem muestra precio automático al elegirlo en venta o cotización.',
      'Reportes de margen usan este precio de venta frente al costo de kardex.',
    ],
    commonErrors: [
      {
        error: 'Producto sin precio al importar guía de salida en venta',
        cause: 'Falta fila en lista de precios del POS.',
        solution: 'Complete precio en Lista de precios y vuelva a importar referencias.',
      },
      {
        error: 'Precio distinto entre POS',
        cause: 'Cada POS tiene su propia lista.',
        solution: 'Repita la configuración en cada punto de venta o copie según procedimiento interno.',
      },
    ],
    relatedFunctions: ['ventas-orders-form', 'ventas-cotizacion-form', 'ventas-report-productos'],
    observations: ['Revise tipos de precio de venta si su empresa usa varios tipos por producto.'],
    screenshots: [SHOTS.listado, SHOTS.formularioCompleto],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-pos-series',
    name: 'Serie por punto de venta',
    type: 'form',
    route: '/ventas/pos-series-config',
    locationPath: 'Ventas > Configuración > Serie por punto de venta',
    generalDescription:
      'Asocia series de numeración (boleta, factura, notas u otros tipos configurados) a cada punto de venta. Sin serie para un tipo de comprobante, Registrar venta bloqueará la emisión con numeración automática hasta que configure la serie o use numeración manual.',
    prerequisites: [
      'Series definidas en Gestión → Configuración → Series autonumeradas.',
      'Punto de venta creado en Gestión → Puntos de venta.',
    ],
    interfaceElements: [
      {
        name: 'Tabla POS × tipo de comprobante',
        controlType: 'Tabla editable',
        required: false,
        editable: true,
        automatic: false,
        description: 'Cada fila vincula un POS con la serie que usará al emitir.',
        behavior: 'Al guardar, la venta en modo automático toma el siguiente número de esa serie.',
      },
      {
        name: 'Serie',
        controlType: 'Select',
        required: true,
        editable: true,
        automatic: false,
        description: 'Código de serie autorizado para el comprobante.',
        example: 'F001',
      },
      {
        name: 'Guardar cambios',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Aplica la configuración para todos los POS editados.',
        behavior: 'No renumera comprobantes ya emitidos.',
      },
    ],
    procedure: [
      'Ventas → Configuración → Serie por punto de venta.',
      'Localice el punto de venta en la tabla.',
      'Para cada tipo de comprobante que vaya a emitir, elija la serie correspondiente.',
      'Guarde los cambios.',
      'En Registrar venta verifique que el tipo de documento ya no muestra error de serie faltante.',
    ],
    practicalExample: {
      context: 'Nuevo POS con serie de boleta propia.',
      data: {
        POS: 'Tienda principal',
        Boleta: 'B001',
        Factura: 'F001',
      },
      steps: [
        'Asigne B001 a boleta y F001 a factura para Tienda principal.',
        'Guarde.',
        'Emita una boleta de prueba con numeración automática.',
      ],
    },
    validations: [
      'Cada tipo de comprobante usado en ventas debe tener serie asignada por POS (salvo numeración manual).',
    ],
    expectedResult: [
      'Registrar venta muestra serie y número correlativo al elegir tipo de documento.',
    ],
    commonErrors: [
      {
        error: 'Mensaje «no tiene serie configurada para este comprobante» al vender',
        cause: 'Falta fila en esta pantalla para ese POS y tipo.',
        solution: 'Complete la serie aquí o use numeración manual temporalmente.',
      },
    ],
    relatedFunctions: ['ventas-orders-form', 'ventas-pos-almacen'],
    observations: [],
    screenshots: [SHOTS.listado, SHOTS.confirmacion],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-pos-almacen',
    name: 'Almacén por punto de venta',
    type: 'form',
    route: '/ventas/pos-warehouse-config',
    locationPath: 'Ventas > Configuración > Almacén por punto de venta',
    generalDescription:
      'Indica desde qué almacén se descuenta el inventario al confirmar una venta con productos para cada punto de venta. Un POS sin almacén asignado puede impedir registrar ventas de productos inventariables o generar movimientos en el almacén incorrecto.',
    prerequisites: [
      'Almacenes creados en Gestión → Configuración → Almacenes.',
      'Kardex activo para los productos que venderá.',
    ],
    interfaceElements: [
      {
        name: 'Listado de puntos de venta',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Muestra cada POS de la empresa.',
        behavior: 'Permite asignar un almacén por fila.',
      },
      {
        name: 'Almacén de salida',
        controlType: 'Select',
        required: true,
        editable: true,
        automatic: false,
        description: 'Almacén del que sale la mercadería al facturar productos.',
        behavior: 'Valida stock disponible en ese almacén al registrar la venta.',
      },
    ],
    procedure: [
      'Ventas → Configuración → Almacén por punto de venta.',
      'Para cada POS seleccione el almacén de despacho habitual.',
      'Guarde.',
      'Registre una venta de producto y verifique el movimiento en kardex del almacén elegido.',
    ],
    practicalExample: {
      context: 'POS de cafetería despacha desde almacén de tienda.',
      data: {
        POS: 'Cafetería HQ',
        Almacén: 'Almacén tienda HQ',
      },
      steps: ['Asigne el almacén', 'Guarde', 'Venda un producto inventariable y revise kardex'],
    },
    validations: ['Almacén obligatorio por POS que vende productos con stock.'],
    expectedResult: ['Ventas de productos generan salida de kardex en el almacén configurado.'],
    commonErrors: [
      {
        error: 'Stock insuficiente al vender',
        cause: 'El producto no tiene saldo en el almacén del POS.',
        solution: 'Transfiera stock o cambie almacén del POS si corresponde operativamente.',
      },
    ],
    relatedFunctions: ['ventas-orders-form', 'ventas-lista-precios'],
    observations: ['Servicios puros no consumen stock aunque el POS tenga almacén.'],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-pos-proyecto',
    name: 'Punto de venta a proyecto',
    type: 'form',
    route: '/ventas/pos-project-config',
    locationPath: 'Ventas > Configuración > Punto de venta a proyecto',
    generalDescription:
      'Vincula cada punto de venta con un proyecto por defecto para las líneas de venta y cotización. Cuando está activo el módulo de proyecto en productos, las líneas nuevas heredan ese proyecto y los reportes de resultados agrupan ingresos por centro de costo.',
    prerequisites: [
      'Flag de proyecto en productos habilitado (módulo / configuración de empresa).',
      'Proyectos creados en Gestión → Configuración → Proyectos.',
    ],
    interfaceElements: [
      {
        name: 'POS',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Listado de puntos de venta.',
        behavior: 'Una fila por POS.',
      },
      {
        name: 'Proyecto por defecto',
        controlType: 'Select',
        required: false,
        editable: true,
        automatic: false,
        description: 'Proyecto que se sugiere en líneas de venta de ese POS.',
        behavior: 'El usuario puede cambiar proyecto en cada línea si tiene permiso.',
      },
    ],
    procedure: [
      'Ventas → Configuración → Punto de venta a proyecto.',
      'Elija el proyecto por defecto para cada POS.',
      'Guarde.',
      'En una venta nueva verifique que las líneas de producto traen el proyecto asignado.',
    ],
    practicalExample: {
      context: 'POS de obra asignado al proyecto «Ampliación planta 2026».',
      data: { POS: 'Obra sur', Proyecto: 'Ampliación planta 2026' },
      steps: ['Asigne proyecto', 'Guarde', 'Registre venta y confirme proyecto en línea'],
    },
    validations: ['Proyecto debe existir y estar activo.'],
    expectedResult: ['Líneas de venta con proyecto coherente al POS para reportes de margen.'],
    commonErrors: [
      {
        error: 'No veo el menú Punto de venta a proyecto',
        cause: 'Módulo de proyecto en productos desactivado.',
        solution: 'Active el flag correspondiente en Habilitación de módulos.',
      },
    ],
    relatedFunctions: ['ventas-orders-form', 'ventas-report-margenes'],
    observations: ['Solo visible cuando productsProjectEnabled está activo.'],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
    isVisible: (ctx) => ctx.settings.productsProjectEnabled,
  }),

  functionDoc({
    id: 'ventas-cotizacion-list',
    name: 'Cotización — listado',
    type: 'list',
    route: '/ventas/quotes',
    locationPath: 'Ventas > Cotización',
    generalDescription:
      'Listado de cotizaciones comerciales por punto de venta: borradores y emitidas, con código, cliente, montos y estado. Desde aquí abre una cotización para editar, emitir, imprimir PDF o convertir su contenido en referencia de una venta u OSV.',
    prerequisites: ['Clientes y lista de precios del POS.', 'Punto de venta seleccionado.'],
    interfaceElements: [
      {
        name: 'Filtros por estado y fecha',
        controlType: 'Select / fechas',
        required: false,
        editable: true,
        automatic: false,
        description: 'Acota cotizaciones borrador vs emitidas.',
        behavior: 'Solo las emitidas suelen importarse como referencia en otros documentos.',
      },
      {
        name: 'Tabla de cotizaciones',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Código, cliente, total, estado, fecha.',
        behavior: 'Acciones: ver, editar, PDF según estado y permisos.',
      },
      {
        name: 'Nueva cotización',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Abre el formulario comercial en modo cotización.',
      },
    ],
    procedure: [
      'Ventas → Cotización.',
      'Use filtros si busca una propuesta antigua.',
      'Pulse Nueva para crear o abra una fila para continuar un borrador.',
      'Para imprimir, use la acción PDF en la fila o dentro del formulario emitido.',
    ],
    practicalExample: {
      context: 'Seguimiento de propuestas del mes.',
      data: { Filtro: 'Emitidas', Mes: 'Octubre 2026' },
      steps: ['Filtre por mes', 'Exporte o abra cada cotización para el cliente'],
    },
    validations: ['Las cotizaciones anuladas no deben usarse como referencia de venta.'],
    expectedResult: ['Listado actualizado con estados correctos.'],
    commonErrors: [
      {
        error: 'No puedo editar una cotización emitida',
        cause: 'Regla de negocio: emitida queda bloqueada salvo flujos de anulación.',
        solution: 'Duplique o cree nueva cotización; o anule según permiso.',
      },
    ],
    relatedFunctions: ['ventas-cotizacion-form', 'ventas-orders-form'],
    observations: [],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-cotizacion-form',
    name: 'Cotización — nueva y edición',
    type: 'form',
    route: '/ventas/quotes/create',
    locationPath: 'Ventas > Cotización > Nueva',
    generalDescription:
      'Documento comercial previo a la venta: cabecera (cliente, fechas, moneda, IGV), grilla de productos y servicios, totales y observaciones. No mueve stock ni emite comprobante fiscal hasta convertirse en venta u OSV. Debe indicar cronograma: fecha de entrega (bienes) o fecha inicio y fin de servicio (servicios por período), igual que en OSV y venta.',
    prerequisites: ['Cliente registrado.', 'Precios en lista del POS para los ítems.'],
    interfaceElements: [
      {
        name: 'Cliente',
        controlType: 'Selector con búsqueda',
        required: true,
        editable: true,
        automatic: false,
        description: 'Receptor de la propuesta.',
        behavior: 'Obligatorio para emitir.',
      },
      {
        name: 'Fecha de emisión / validez',
        controlType: 'Fecha',
        required: true,
        editable: true,
        automatic: false,
        description: 'Emisión y hasta cuándo rigen los precios.',
        behavior: 'Validez informa al cliente el plazo de la oferta.',
      },
      {
        name: 'Grilla de ítems',
        controlType: 'Tabla editable',
        required: true,
        editable: true,
        automatic: false,
        description: 'Productos y servicios con cantidad, precio unitario sin IGV en pantalla, flag IGV.',
        behavior:
          'Subtotales y totales se recalculan al cambiar cantidad o precio. Véase Inicio y acceso → Grilla de productos e ítems y Ventas → Grilla — Ventas.',
      },
      {
        name: 'Fecha de entrega',
        controlType: 'Fecha',
        required: false,
        editable: true,
        automatic: false,
        description: 'Para venta de bienes; alternativa al par de fechas de servicio.',
        behavior: 'Si se completa, satisface validación de cronograma junto con fechas de servicio.',
      },
      {
        name: 'Fechas de servicio (inicio / fin)',
        controlType: 'Fecha',
        required: false,
        editable: true,
        automatic: false,
        description: 'Período de prestación de servicios.',
        behavior: 'Fin debe ser mayor o igual que inicio; se copian a venta si importa referencias.',
      },
      {
        name: 'Emitir / Guardar borrador',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Borrador permite seguir editando; emitida queda lista para referenciar.',
      },
    ],
    procedure: [
      'Ventas → Cotización → Nueva.',
      'Seleccione cliente y complete cabecera (moneda, tasa IGV, observaciones).',
      'Agregue líneas: producto o servicio, cantidad, revise precio de lista.',
      'Complete fecha de entrega o ambas fechas de servicio.',
      'Guarde borrador o Emita.',
      'Imprima PDF para el cliente si lo requiere.',
    ],
    practicalExample: {
      context: 'Propuesta de catering por cinco días.',
      data: {
        Cliente: 'Constructora ABC',
        Servicio: 'Almuerzo ejecutivo',
        'Inicio servicio': '2026-11-01',
        'Fin servicio': '2026-11-05',
      },
      steps: [
        'Cree cotización con líneas de servicio.',
        'Indique fechas de servicio del 1 al 5 de noviembre.',
        'Emita y envíe PDF.',
      ],
    },
    validations: [
      'Cliente y al menos un ítem obligatorios.',
      'Debe existir fecha de entrega o par completo de fechas de servicio si hay ítems.',
      'Cantidad mayor que cero y precio válido por línea.',
    ],
    expectedResult: [
      'Cotización emitida con código y totales coherentes.',
      'Disponible en Referencias de Registrar venta para importar ítems pendientes.',
    ],
    commonErrors: [
      {
        error: '«Debe ingresar la fecha de entrega o las fechas de servicio»',
        cause: 'Cronograma vacío con ítems en la grilla.',
        solution: 'Complete entrega o inicio/fin de servicio.',
      },
    ],
    relatedFunctions: [
      'ventas-cotizacion-list',
      'ventas-orders-form',
      'ventas-osv-form',
      'ventas-grilla-productos-items',
    ],
    observations: ['Los precios de línea en cotización se muestran sin IGV aunque la lista incluya IGV.'],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-grilla-productos-items',
    name: 'Grilla de productos e ítems — Ventas',
    type: 'screen',
    route: '/ventas/orders/create',
    locationPath:
      'Ventas > Cotización / Orden de servicio (venta) / Registrar venta → bloque «Ítems»',
    generalDescription:
      'Uso de la grilla compartida en el formulario comercial de Ventas (VentasCommercialForm). La guía general de columnas, captura rápida e importación Excel está en Inicio y acceso → «Grilla de productos e ítems». Aquí se resumen las reglas solo de Ventas: precios de línea siempre sin IGV en pantalla; líneas de servicio con descripción obligatoria; catálogo con ofertas y tipo de venta por POS; importación Excel en cotización y registrar venta (no en OSV); al facturar, productos descuentan stock del almacén del POS.',
    prerequisites: [
      'Punto de venta seleccionado en la barra superior.',
      'Productos y servicios activos en Gestión; precios cargados en la lista de precios del POS.',
      'Para vender productos con stock: almacén asignado al POS (Ventas > Configuración > Almacén por punto de venta).',
      'Opcional: tipos de venta, proyectos por línea y ofertas según configuración de la empresa.',
    ],
    interfaceElements: [
      {
        name: 'Bloque «Ítems»',
        controlType: 'Sección',
        required: false,
        editable: true,
        automatic: false,
        description: '+ Producto, + Servicio, Excel (cotización/venta), captura rápida y filas de producto/servicio.',
        behavior: 'Columnas comunes: véase «Grilla de productos e ítems» en Inicio y acceso.',
      },
      {
        name: 'VentasCatalogSelector / ofertas',
        controlType: 'Buscador',
        required: true,
        editable: true,
        automatic: false,
        description: 'Catálogo del POS con productos y ofertas; precio de lista a neto en pantalla.',
        behavior: 'Ofertas multi-producto no permiten cambiar tipo de venta en la misma fila.',
      },
      {
        name: 'Referencias (solo registrar venta)',
        controlType: 'Modal',
        required: false,
        editable: true,
        automatic: false,
        description: 'Importa líneas pendientes de cotización, OSV o guía de salida con evento venta.',
        behavior: 'Las líneas se agregan al final de la grilla; no duplica ítems ya vinculados.',
      },
      {
        name: 'Modal Referencias y totales',
        controlType: 'Resumen',
        required: false,
        editable: false,
        automatic: true,
        description: 'Totales bajo la grilla; en venta, enlace «Ajustar montos» para IGV total.',
        behavior: 'OSV: solo filas de servicio (Servicio + Descripción obligatoria).',
      },
    ],
    procedure: [
      'Lea primero Inicio y acceso → Grilla de productos e ítems (columnas, Excel, captura rápida).',
      'Abra cotización, OSV o registrar venta.',
      'Cargue líneas manualmente, por captura rápida o Excel (no Excel en OSV).',
      'En venta, use Referencias si trae ítems de cotización/OSV/guía.',
      'Complete cronograma (entrega o fechas de servicio) y registre o emita.',
    ],
    practicalExample: {
      context: 'Factura de almacén con dos productos y un servicio de delivery.',
      data: {
        POS: 'Tienda principal',
        Producto_1: 'Gaseosa 600 ml × 24 und',
        Producto_2: 'Snack mix × 5 und',
        Servicio: 'Delivery urbano — descripción en línea',
        IGV: '18 % en cabecera, líneas gravadas',
      },
      steps: [
        'Registrar venta → cliente y factura.',
        'Captura rápida: agregue las dos bebidas y snacks con cantidades del pedido.',
        '+ Servicio: elija «Delivery», descripción «Reparto zona A», cantidad 1 y precio de lista.',
        'Verifique total con IGV y registre la venta.',
      ],
    },
    validations: [
      'Al menos una línea con cantidad mayor que cero antes de emitir o registrar.',
      'Cada línea de producto requiere producto seleccionado; cada línea de servicio requiere servicio y descripción.',
      'Precio unitario numérico válido; tipo de venta obligatorio cuando el selector está visible.',
      'Importación Excel: productos deben resolverse por código/SKU; cantidades y precios válidos.',
      'En venta con productos: stock suficiente en el almacén del POS (validación al registrar, no al tipear).',
      'Líneas importadas desde cotización/OSV no deben superar la cantidad pendiente del documento origen.',
    ],
    expectedResult: [
      'Listado de ítems coherente con lo ofertado o facturado; totales cuadran con la suma de líneas.',
      'Tras emitir cotización/OSV, las cantidades pendientes quedan para importar en venta.',
      'Tras registrar venta, kardex refleja salidas de productos y OSV/cotización actualizan cantidades facturadas.',
    ],
    commonErrors: [
      {
        error: '«No hay ítems» o no deja guardar',
        cause: 'Grilla vacía o todas las cantidades en cero.',
        solution: 'Agregue al menos una línea completa con cantidad positiva.',
      },
      {
        error: 'Precio en cero o muy distinto a la lista',
        cause: 'Producto sin precio en lista del POS o edición manual.',
        solution: 'Revise lista de precios del POS; complete precio antes de emitir.',
      },
      {
        error: 'Falta descripción en servicio',
        cause: 'Campo Descripción vacío en fila de servicio.',
        solution: 'Escriba el texto que debe imprimirse en PDF/comprobante.',
      },
      {
        error: 'Importación Excel no agrega filas',
        cause: 'SKU inexistente, archivo distinto a la plantilla o errores en previsualización no confirmados.',
        solution: 'Descargue plantilla oficial, corrija códigos y confirme la importación en el modal.',
      },
      {
        error: 'Stock insuficiente al registrar venta',
        cause: 'Cantidad en grilla supera saldo en almacén del POS.',
        solution: 'Reduzca cantidad, transfiera stock o cambie almacén del POS si procede operativamente.',
      },
      {
        error: 'No veo + Producto ni captura rápida',
        cause: 'Está en OSV (solo servicios) o captura rápida reemplaza el botón + Producto.',
        solution: 'En OSV use + Servicio; en venta active/desactive línea rápida según política del administrador.',
      },
    ],
    relatedFunctions: [
      'general-grilla-productos-items',
      'general-grilla-detalle-ventas-producto',
      'general-grilla-detalle-ventas-servicio',
      'general-grilla-detalle-controles-comunes',
      'ventas-cotizacion-form',
      'ventas-osv-form',
      'ventas-orders-form',
      'ventas-lista-precios',
      'ventas-pos-almacen',
    ],
    observations: [
      'Detalle de cada input/select: Inicio y acceso → «Grilla — fila Producto (Ventas)» y «Grilla — fila Servicio (Ventas)».',
      ROLES.limitado,
    ],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-osv-list',
    name: 'Orden de servicio (venta) — listado',
    type: 'list',
    route: '/ventas/service-orders',
    locationPath: 'Ventas > Orden de servicio (venta)',
    generalDescription:
      'Consulta las órdenes de servicio de venta (OSV): compromisos de prestar servicios con control de cantidad pendiente de facturar. Muestra avance de cumplimiento y permite abrir el detalle, PDF o enlazar a ventas.',
    prerequisites: ['Servicios en catálogo y precios en lista del POS.'],
    interfaceElements: [
      {
        name: 'Filtros',
        controlType: 'Fechas / estado / cliente',
        required: false,
        editable: true,
        automatic: false,
        description: 'Localiza OSV emitidas o en borrador.',
        behavior: 'Las emitidas con saldo aparecen en Referencias de venta.',
      },
      {
        name: 'Columna de avance',
        controlType: 'Indicador',
        required: false,
        editable: false,
        automatic: true,
        description: 'Porcentaje facturado respecto al total de la OSV.',
        behavior: 'OSV al 100 % no se ofrece en referencias.',
      },
      {
        name: 'Nueva OSV',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Abre formulario de OSV.',
      },
    ],
    procedure: [
      'Ventas → Orden de servicio (venta).',
      'Filtre por cliente o período si necesita.',
      'Abra una OSV para ver detalle o pulse Nueva.',
      'Use imprimir/PDF desde la fila o el formulario.',
    ],
    practicalExample: {
      context: 'Control de servicios pendientes de facturar del mes.',
      data: { Filtro: 'Emitidas', Avance: '< 100 %' },
      steps: ['Liste OSV', 'Identifique las que requieren factura parcial'],
    },
    validations: [],
    expectedResult: ['Vista clara de OSV y su estado de facturación.'],
    commonErrors: [
      {
        error: 'OSV no aparece al facturar',
        cause: 'Cliente distinto en venta, OSV no emitida o ya facturada al 100 %.',
        solution: 'Verifique cliente, estado emitido y saldo pendiente.',
      },
    ],
    relatedFunctions: ['ventas-osv-form', 'ventas-orders-form'],
    observations: [],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-osv-form',
    name: 'Orden de servicio (venta) — nueva y edición',
    type: 'form',
    route: '/ventas/service-orders/create',
    locationPath: 'Ventas > Orden de servicio (venta) > Nueva',
    generalDescription:
      'Registra el compromiso de prestar servicios: cabecera con cliente y fechas de inicio/fin del servicio, líneas solo de servicios con cantidad, descripción, precio e IGV. Al emitir, las líneas quedan con cantidad pendiente para importar en Registrar venta; al vincular la OSV, esas fechas de servicio pasan al formulario de venta si aún estaban vacías.',
    prerequisites: ['Cliente y servicios con precio en lista del POS.'],
    interfaceElements: [
      {
        name: 'Cliente',
        controlType: 'Selector',
        required: true,
        editable: true,
        automatic: false,
        description: 'Debe coincidir al facturar; referencias filtran por cliente.',
      },
      {
        name: 'Línea — servicio',
        controlType: 'Selector',
        required: true,
        editable: true,
        automatic: false,
        description: 'Servicio del catálogo; trae unidad y precio por defecto.',
      },
      {
        name: 'Descripción de línea',
        controlType: 'Texto',
        required: true,
        editable: true,
        automatic: false,
        description: 'Detalle en PDF (plato, alcance, notas).',
        example: 'Menú ejecutivo — pollo a la plancha',
      },
      {
        name: 'Fechas de servicio (cabecera)',
        controlType: 'Fecha inicio / fin',
        required: false,
        editable: true,
        automatic: false,
        description: 'Período de prestación del servicio.',
        behavior: 'Se propagan a la venta al aceptar referencias OSV si la venta no tenía fechas.',
      },
      {
        name: 'Referencias (desde cotización)',
        controlType: 'Modal',
        required: false,
        editable: true,
        automatic: false,
        description: 'En OSV puede importar servicios pendientes de una cotización emitida.',
        behavior: 'Solo ítems de servicio pendientes de la cotización seleccionada.',
      },
    ],
    procedure: [
      'Ventas → Orden de servicio (venta) → Nueva.',
      'Seleccione cliente y condiciones de cabecera.',
      'Opcional: Referencias → cotización emitida → importe servicios pendientes.',
      'Agregue o ajuste líneas de servicio con descripción.',
      'Indique fechas de inicio y fin del servicio.',
      'Emita la OSV e imprima si corresponde.',
    ],
    practicalExample: {
      context: 'Contrato de limpieza mensual.',
      data: {
        Servicio: 'Limpieza industrial',
        Cantidad: '30 días',
        'Inicio': '2026-10-01',
        'Fin': '2026-10-31',
      },
      steps: ['Complete fechas de octubre', 'Emita OSV', 'Facture parcial desde venta'],
    },
    validations: [
      'Al menos un servicio en la OSV.',
      'Descripción obligatoria por línea de servicio.',
      'Cronograma: entrega o fechas de servicio si hay ítems.',
    ],
    expectedResult: [
      'OSV emitida con código y saldo pendiente de facturación.',
      'Líneas vinculables en Registrar venta sin duplicar ítems ya facturados.',
    ],
    commonErrors: [
      {
        error: 'No importa líneas desde cotización',
        cause: 'Cotización sin servicios pendientes o no emitida.',
        solution: 'Emita cotización y verifique cantidades pendientes.',
      },
    ],
    relatedFunctions: [
      'ventas-osv-list',
      'ventas-orders-form',
      'ventas-cotizacion-form',
      'ventas-grilla-productos-items',
    ],
    observations: [
      'En concesionaria la descripción suele mostrar el nombre del plato/comida.',
      'La grilla en OSV solo admite servicios; el detalle de columnas compartidas está en «Grilla de productos e ítems».',
    ],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-servicios-continuos',
    name: 'Servicios continuos (venta)',
    type: 'list',
    route: '/ventas/continuous-services',
    locationPath: 'Ventas > Servicios',
    generalDescription:
      'Administra contratos o servicios continuos de venta (abonos, suministro recurrente): registro del acuerdo con cliente, periodicidad y montos. En Registrar venta puede vincularse como referencia para trazabilidad; el modal indica que los servicios continuos no copian ítems automáticamente a la grilla.',
    prerequisites: ['Cliente y servicios definidos.', 'Punto de venta seleccionado.'],
    interfaceElements: [
      {
        name: 'Listado de servicios continuos',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Código, cliente, estado abierto/cerrado, montos.',
        behavior: 'Solo abiertos se ofrecen en referencias de venta.',
      },
      {
        name: 'Nuevo servicio continuo',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Formulario de alta del acuerdo.',
      },
      {
        name: 'Estado',
        controlType: 'Etiqueta',
        required: false,
        editable: true,
        automatic: false,
        description: 'Abierto permite vincular; cerrado es histórico.',
      },
    ],
    procedure: [
      'Ventas → Servicios.',
      'Cree un nuevo servicio continuo con cliente y condiciones.',
      'Mantenga estado abierto mientras el contrato esté vigente.',
      'En cada facturación periódica abra Registrar venta → Referencias y marque el servicio continuo.',
      'Agregue manualmente las líneas de cobro del período y registre la venta.',
    ],
    practicalExample: {
      context: 'Mantenimiento mensual facturado el día 1.',
      data: { Cliente: 'Hotel XYZ', Periodicidad: 'Mensual', Estado: 'Abierto' },
      steps: [
        'Registre el servicio continuo abierto.',
        'Cada mes vincúlelo en venta y cargue el monto del mes.',
      ],
    },
    validations: ['Cliente obligatorio.', 'Cierre solo cuando termine el contrato.'],
    expectedResult: ['Trazabilidad entre ventas periódicas y el mismo acuerdo.'],
    commonErrors: [
      {
        error: 'Esperaba que importara líneas al marcar servicio continuo',
        cause: 'Comportamiento diseñado: no copia ítems, solo vincula.',
        solution: 'Agregue líneas de servicio manualmente en la venta.',
      },
    ],
    relatedFunctions: ['ventas-orders-form'],
    observations: ['Analogía con Servicios continuos de Compras pero orientado a ingresos.'],
    screenshots: [SHOTS.listado, SHOTS.formularioCompleto],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-orders-list',
    name: 'Ventas — listado de comprobantes',
    type: 'list',
    route: '/ventas/orders',
    locationPath: 'Ventas > Ventas',
    generalDescription:
      'Historial de comprobantes de venta (boletas, facturas y otros tipos configurados) del punto de venta: filtros por fecha, cliente y tipo, totales, estado y acceso a PDF o anulación según permisos. No incluye la ruta de importación masiva, que es un ítem de menú aparte.',
    prerequisites: ['Ventas ya registradas en el sistema o listado vacío en instalación nueva.'],
    interfaceElements: [
      {
        name: 'Filtros de fecha y documento',
        controlType: 'Fechas / select',
        required: false,
        editable: true,
        automatic: false,
        description: 'Acota el período y tipo de comprobante.',
      },
      {
        name: 'Tabla de ventas',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Serie-número, cliente, total, moneda, estado.',
        behavior: 'Muestra badge de moneda si opera dual currency.',
      },
      {
        name: 'Nueva venta',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Navega a Registrar venta.',
      },
    ],
    procedure: [
      'Ventas → Ventas.',
      'Ajuste filtros al período deseado.',
      'Abra una fila para ver detalle o reimprimir.',
      'Pulse Nueva venta para emitir otro comprobante.',
    ],
    practicalExample: {
      context: 'Cierre diario: revisar boletas del día.',
      data: { Fecha: 'Hoy', Tipo: 'Boleta' },
      steps: ['Filtre por fecha del día', 'Cuadre totales con caja'],
    },
    validations: [],
    expectedResult: ['Listado coherente con lo registrado en caja y reportes.'],
    commonErrors: [
      {
        error: 'Venta no aparece',
        cause: 'POS distinto al seleccionado o fecha fuera de filtro.',
        solution: 'Cambie POS en barra superior o amplíe rango de fechas.',
      },
    ],
    relatedFunctions: ['ventas-orders-form', 'ventas-report-registro'],
    observations: ['Turnos de caja cerrados pueden bloquear nuevas ventas con esa fecha de emisión.'],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-orders-form',
    name: 'Registrar venta',
    type: 'form',
    route: '/ventas/orders/create',
    locationPath: 'Ventas > Ventas > Nueva',
    generalDescription:
      'Formulario comercial principal (VentasCommercialForm) para emitir boleta, factura u otro comprobante: cabecera con cliente, tipo de documento, serie, fecha de emisión, moneda y condición de pago; modal Referencias para cotizaciones emitidas, OSV con saldo, guías de salida con evento venta y servicios continuos abiertos; grilla de ítems; totales; observaciones; bloque de fechas de entrega o de servicio. Al aceptar referencias se agregan líneas al final, se omite lo ya vinculado y las fechas de cotización/OSV rellenan cabecera solo si estaban vacías. Validación de cronograma: debe completar fecha de entrega o ambas fechas de servicio antes de registrar. Productos descuentan stock del almacén del POS; líneas OSV guardan vínculo para no facturar dos veces la misma cantidad.',
    prerequisites: [
      'Cliente, serie por POS, almacén por POS y precios de lista.',
      'Punto de venta seleccionado.',
      'Para productos: stock en almacén del POS.',
    ],
    interfaceElements: [
      {
        name: 'Cliente',
        controlType: 'Selector + Nuevo cliente',
        required: true,
        editable: true,
        automatic: false,
        description: 'Receptor del comprobante; filtra documentos en Referencias.',
        behavior: 'Puede crear cliente rápido desde modal sin salir del formulario.',
      },
      {
        name: 'Tipo de documento / serie / número',
        controlType: 'Select y texto',
        required: true,
        editable: true,
        automatic: true,
        description: 'Boleta, factura, etc.; numeración automática según serie del POS o manual.',
        behavior: 'Sin serie configurada muestra error hasta configurar o usar modo manual.',
      },
      {
        name: 'Referencias (modal)',
        controlType: 'Modal con pestañas/listas',
        required: false,
        editable: true,
        automatic: false,
        description:
          'Cotizaciones: importa ítems pendientes. OSV: importa servicios pendientes y mide avance con esta venta. Guías de salida: solo productos con evento kardex «venta»; guías mixtas avisan cuántos ítems son venta. Servicios continuos: vincula sin copiar líneas.',
        behavior:
          'Filtra por cliente seleccionado. Al aceptar, copia cliente y fechas de entrega/servicio de la primera cotización u OSV nueva solo si esos campos estaban vacíos. Omite ítems de cotización/OSV ya importados.',
      },
      {
        name: 'Grilla de ítems',
        controlType: 'Tabla',
        required: true,
        editable: true,
        automatic: false,
        description: 'Productos y servicios; precio unitario sin IGV en pantalla; proyecto y tipo de venta si aplica.',
        behavior:
          'Inicio y acceso → Grilla de productos e ítems; matices de Ventas en «Grilla — Ventas».',
      },
      {
        name: 'Fecha de entrega',
        controlType: 'Fecha',
        required: false,
        editable: true,
        automatic: false,
        description: 'Para bienes; alternativa al par de fechas de servicio.',
        behavior: 'Al completarla, limpia error de cronograma aunque falten fechas de servicio.',
      },
      {
        name: 'Fecha inicio / fin del servicio',
        controlType: 'Fecha',
        required: false,
        editable: true,
        automatic: false,
        description: 'Período de prestación; típico en servicios importados desde OSV.',
        behavior: 'Al importar OSV nuevas, se copian si el formulario aún no tenía esas fechas.',
      },
      {
        name: 'Condición y medio de pago',
        controlType: 'Select',
        required: true,
        editable: true,
        automatic: false,
        description: 'Contado exige medio de pago; algunos medios exigen número de operación.',
      },
      {
        name: 'Totales y ajustar montos',
        controlType: 'Solo lectura / enlace',
        required: false,
        editable: true,
        automatic: true,
        description: 'Base, IGV y total; en USD puede mostrar equivalente PEN con tipo de cambio del día.',
        behavior: 'Ajustar montos permite override controlado del IGV total.',
      },
    ],
    procedure: [
      'Confirme el punto de venta activo en la barra superior.',
      'Ventas → Ventas → Nueva.',
      'Seleccione cliente y tipo de comprobante.',
      'Opcional: pulse Referencias, marque cotización/OSV/guía/servicio continuo y Aceptar; revise líneas importadas y advertencias de guía sin precio.',
      'Agregue o edite líneas manuales; complete descripción en servicios.',
      'Complete fecha de entrega o fechas de servicio (verifique las traídas por OSV).',
      'Indique medio de pago y número de operación si aplica.',
      'Revise totales y pulse Registrar venta.',
    ],
    practicalExample: {
      context: 'Facturar parte de una OSV de catering ya ejecutada.',
      data: {
        Cliente: 'Eventos Lima SAC',
        OSV: 'OSV-2026-0142',
        'Fechas servicio': 'Importadas de OSV si estaban vacías',
        Comprobante: 'Factura F001-000123',
      },
      steps: [
        'Seleccione el cliente de la OSV.',
        'Referencias → marque la OSV → Aceptar.',
        'Confirme fechas de servicio en el pie del formulario.',
        'Registre la factura.',
      ],
    },
    validations: [
      'Cliente, fecha de emisión, POS, al menos un ítem, tipo de documento.',
      'Serie configurada o numeración manual completa.',
      'Medio de pago en contado; número de operación si el medio lo exige.',
      'No registrar en fecha de turno de caja ya cerrado.',
      'Cronograma: fecha de entrega o inicio y fin de servicio obligatorios con ítems.',
      'Líneas: producto o servicio, cantidad > 0, precio válido, descripción en servicios.',
    ],
    expectedResult: [
      'Comprobante registrado con numeración correlativa.',
      'Salida de kardex por productos vendidos.',
      'OSV actualiza cantidad facturada; cotización reduce pendientes.',
    ],
    commonErrors: [
      {
        error: '«Debe ingresar la fecha de entrega o las fechas de servicio»',
        cause: 'Pie de cronograma vacío.',
        solution: 'Use entrega para bienes o inicio/fin para servicios; revise import OSV.',
      },
      {
        error: 'Productos sin precio en guía de salida',
        cause: 'Lista de precios incompleta.',
        solution: 'Configure precio en Lista de precios y reimporte o agregue líneas manualmente.',
      },
      {
        error: '«El turno de este día ya fue cerrado»',
        cause: 'Liquidación de caja cerró esa fecha.',
        solution: 'Use otra fecha de emisión o gestione reapertura según política interna.',
      },
    ],
    relatedFunctions: [
      'ventas-osv-form',
      'ventas-cotizacion-form',
      'ventas-grilla-productos-items',
      'ventas-lista-precios',
      'ventas-pos-series',
      'ventas-liquidacion-caja',
    ],
    observations: [
      'Guías con evento distinto de venta se omiten con mensaje informativo.',
      'No marque dos veces la misma OSV esperando duplicar líneas: el sistema omite ítems ya vinculados.',
    ],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion, SHOTS.vistaGeneral],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-bulk-import',
    name: 'Importación masiva de ventas',
    type: 'form',
    route: '/ventas/orders/bulk-import',
    locationPath: 'Ventas > Importar masiva',
    generalDescription:
      'Carga muchas ventas desde archivo Excel cuando el volumen diario es alto. La plantilla oficial define columnas obligatorias (cliente, ítems, POS, fechas, etc.); tras subir el archivo el sistema valida fila por fila y muestra un resumen antes de confirmar, sin importar filas inválidas.',
    prerequisites: [
      'Clientes, productos/servicios y precios ya configurados.',
      'Plantilla descargada desde la misma pantalla.',
    ],
    interfaceElements: [
      {
        name: 'Descargar plantilla',
        controlType: 'Enlace / botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Excel con encabezados que no deben renombrarse.',
      },
      {
        name: 'Subir archivo',
        controlType: 'Input archivo',
        required: true,
        editable: true,
        automatic: false,
        description: 'Formato .xlsx según plantilla.',
        behavior: 'Muestra resumen de filas válidas e inválidas.',
      },
      {
        name: 'Resumen de validación',
        controlType: 'Panel',
        required: false,
        editable: false,
        automatic: true,
        description: 'Errores por fila con mensaje legible.',
      },
      {
        name: 'Confirmar importación',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Crea ventas solo de filas aceptadas.',
      },
    ],
    procedure: [
      'Ventas → Importar masiva.',
      'Descargue la plantilla actual.',
      'Complete filas sin alterar nombres de columnas.',
      'Suba el archivo y lea el resumen de errores.',
      'Corrija el Excel si hay filas rechazadas y vuelva a subir.',
      'Confirme importación cuando el resumen sea aceptable.',
    ],
    practicalExample: {
      context: 'Carga nocturna de ventas de POS externos.',
      data: { Filas: '150', Errores: '3 por cliente inexistente' },
      steps: ['Corrija los 3 clientes', 'Reimporte solo esas filas o archivo completo'],
    },
    validations: [
      'Códigos de cliente y producto deben existir.',
      'POS y fechas coherentes con reglas de venta.',
    ],
    expectedResult: ['Ventas creadas visibles en listado y reportes.'],
    commonErrors: [
      {
        error: 'Todas las filas fallan',
        cause: 'Encabezados de columna modificados o archivo equivocado.',
        solution: 'Vuelva a descargar plantilla y copie datos.',
      },
    ],
    relatedFunctions: ['ventas-orders-list', 'ventas-clientes-form'],
    observations: ['Revise turnos de caja cerrados para las fechas importadas.'],
    screenshots: [SHOTS.listado, SHOTS.confirmacion],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-liquidacion-caja',
    name: 'Liquidación de caja',
    type: 'form',
    route: '/ventas/caja',
    locationPath: 'Ventas > Liquidación de caja',
    generalDescription:
      'Cierra turno por punto de venta y fecha: muestra ventas registradas en el sistema y permite declarar montos físicos (efectivo, tarjeta, otros). La diferencia queda registrada para auditoría y alimenta el reporte de liquidaciones. Tras cerrar un día, Registrar venta puede bloquear nuevas emisiones con esa fecha de emisión.',
    prerequisites: [
      'Flag ventasCashLiquidationEnabled activo.',
      'Ventas del día ya registradas en el POS.',
    ],
    interfaceElements: [
      {
        name: 'POS y fecha / turno',
        controlType: 'Select / fecha',
        required: true,
        editable: true,
        automatic: false,
        description: 'Ámbito del cierre.',
      },
      {
        name: 'Resumen sistema',
        controlType: 'Solo lectura',
        required: false,
        editable: false,
        automatic: true,
        description: 'Totales de ventas por medio de pago según registros.',
      },
      {
        name: 'Montos declarados',
        controlType: 'Numérico',
        required: true,
        editable: true,
        automatic: false,
        description: 'Lo contado en caja o vouchers.',
        behavior: 'Diferencia = declarado − sistema.',
      },
      {
        name: 'Cerrar turno',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Confirma el cierre y bloquea nuevas ventas en esa fecha para el POS.',
      },
    ],
    procedure: [
      'Ventas → Liquidación de caja.',
      'Seleccione POS y fecha o turno a cerrar.',
      'Compare totales del sistema con arqueo físico.',
      'Ingrese montos declarados por medio de pago.',
      'Cierre el turno y archive comprobantes físicos.',
    ],
    practicalExample: {
      context: 'Cierre de caja con faltante de S/ 5 en efectivo.',
      data: { Declarado: 'S/ 495', Sistema: 'S/ 500', Diferencia: '- S/ 5' },
      steps: ['Registre montos reales', 'Cierre', 'Revise reporte de liquidaciones'],
    },
    validations: ['Montos numéricos; POS y fecha obligatorios.'],
    expectedResult: ['Turno cerrado; historial disponible en reporte de liquidaciones.'],
    commonErrors: [
      {
        error: 'No puedo vender con fecha de ayer',
        cause: 'Turno de esa fecha cerrado en liquidación.',
        solution: 'Use fecha actual o procedimiento interno de reapertura.',
      },
    ],
    relatedFunctions: ['ventas-orders-form', 'ventas-report-liquidaciones'],
    observations: ['Visible solo con módulo de liquidación de caja habilitado.'],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.ventas,
    isVisible: (ctx) => ctx.settings.ventasCashLiquidationEnabled,
  }),

  functionDoc({
    id: 'ventas-report-margenes',
    name: 'Dashboard de márgenes',
    type: 'report',
    route: '/ventas/reports',
    locationPath: 'Ventas > Reportes > Dashboard de márgenes',
    generalDescription:
      'Visualización gráfica y tabular de rentabilidad estimada: compara monto de venta con costo (kardex o última compra según configuración del reporte). Sirve para detectar productos con margen bajo por punto de venta y período.',
    prerequisites: ['Ventas registradas y costos en kardex o compras.'],
    interfaceElements: [
      {
        name: 'Rango de fechas',
        controlType: 'Fecha inicio / fin',
        required: true,
        editable: true,
        automatic: false,
        description: 'Período de análisis.',
      },
      {
        name: 'Punto de venta',
        controlType: 'Select',
        required: false,
        editable: true,
        automatic: false,
        description: 'Filtra un POS o todos los permitidos.',
      },
      {
        name: 'Gráficos y tablas',
        controlType: 'Visualización',
        required: false,
        editable: false,
        automatic: true,
        description: 'Márgenes por producto, categoría o tiempo.',
      },
    ],
    procedure: [
      'Ventas → Reportes → Dashboard de márgenes.',
      'Seleccione fechas y POS.',
      'Espere la carga del gráfico.',
      'Identifique ítems con margen negativo o bajo y cruce con Lista de precios.',
    ],
    practicalExample: {
      context: 'Revisión semanal de cafetería.',
      data: { POS: 'Cafetería', Semana: '20–26 oct 2026' },
      steps: ['Filtre semana', 'Ordene por margen % ascendente'],
    },
    validations: ['Fechas obligatorias; inicio ≤ fin.'],
    expectedResult: ['Vista ejecutiva de rentabilidad sin detalle línea a línea.'],
    commonErrors: [
      {
        error: 'Costos en cero',
        cause: 'Producto sin movimientos de kardex o sin compras.',
        solution: 'Regularice costos en almacén o espere entradas de compra.',
      },
    ],
    relatedFunctions: ['ventas-report-productos', 'ventas-lista-precios'],
    observations: [],
    screenshots: [SHOTS.vistaGeneral, SHOTS.listado],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-report-dashboard-ventas',
    name: 'Dashboard de ventas',
    type: 'report',
    route: '/ventas/reports/ventas',
    locationPath: 'Ventas > Reportes > Dashboard de ventas',
    generalDescription:
      'Indicadores de volumen y monto de ventas por día o mes: vista ejecutiva para gerencia operativa. Complementa al reporte de registro detallado pero no sustituye la conciliación contable línea a línea.',
    prerequisites: ['Ventas emitidas en el período.'],
    interfaceElements: [
      {
        name: 'Período',
        controlType: 'Select / fechas',
        required: true,
        editable: true,
        automatic: false,
        description: 'Mes actual, rango personalizado, etc.',
      },
      {
        name: 'Punto de venta',
        controlType: 'Select',
        required: false,
        editable: true,
        automatic: false,
        description: 'Acota a un POS.',
      },
      {
        name: 'Tarjetas e indicadores',
        controlType: 'Dashboard',
        required: false,
        editable: false,
        automatic: true,
        description: 'Totales, tendencias, comparativos simples.',
      },
    ],
    procedure: [
      'Ventas → Reportes → Dashboard de ventas.',
      'Elija período y POS.',
      'Analice picos y caídas.',
      'Profundice en Reporte de ventas si necesita detalle.',
    ],
    practicalExample: {
      context: 'Reunión mensual de gerencia.',
      data: { Mes: 'Octubre 2026', POS: 'Todos' },
      steps: ['Abra dashboard', 'Capture pantalla para acta'],
    },
    validations: [],
    expectedResult: ['Indicadores cargados sin error.'],
    commonErrors: [
      {
        error: 'Dashboard vacío',
        cause: 'Sin ventas en el filtro o POS incorrecto.',
        solution: 'Amplíe fechas o cambie POS.',
      },
    ],
    relatedFunctions: ['ventas-report-registro', 'ventas-report-margenes'],
    observations: [],
    screenshots: [SHOTS.vistaGeneral],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-report-registro',
    name: 'Reporte de ventas (registro)',
    type: 'report',
    route: '/ventas/reports/registro',
    locationPath: 'Ventas > Reportes > Reporte de ventas',
    generalDescription:
      'Detalle de documentos de venta emitidos para contabilidad y conciliación: cliente, tipo, serie-número, totales, moneda, estado y exportación. Muestra montos en moneda original y equivalente en soles cuando aplica dual currency.',
    prerequisites: ['Ventas registradas.'],
    interfaceElements: [
      {
        name: 'Filtros',
        controlType: 'Fechas, cliente, tipo, POS',
        required: true,
        editable: true,
        automatic: false,
        description: 'Acota el universo del reporte.',
      },
      {
        name: 'Tabla de resultados',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Una fila por documento o por línea según diseño de pantalla.',
      },
      {
        name: 'Exportar',
        controlType: 'Botón',
        required: false,
        editable: true,
        automatic: false,
        description: 'Excel o CSV para contabilidad.',
      },
    ],
    procedure: [
      'Ventas → Reportes → Reporte de ventas.',
      'Defina rango de fechas y filtros opcionales.',
      'Genere el reporte.',
      'Exporte y concilie con caja y SUNAT según su proceso interno.',
    ],
    practicalExample: {
      context: 'Cierre contable de octubre.',
      data: { Desde: '2026-10-01', Hasta: '2026-10-31', Tipo: 'Factura' },
      steps: ['Filtre facturas del mes', 'Exporte a Excel'],
    },
    validations: ['Rango de fechas obligatorio.'],
    expectedResult: ['Archivo o tabla con totales cuadrados al listado de ventas.'],
    commonErrors: [
      {
        error: 'Totales no cuadran con caja',
        cause: 'Medios de pago o notas no incluidas en el filtro.',
        solution: 'Incluya todos los tipos de documento o revise anulaciones.',
      },
    ],
    relatedFunctions: ['ventas-orders-list', 'ventas-report-dashboard-ventas'],
    observations: ['No reemplaza asientos contables; es fuente operativa.'],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-report-productos',
    name: 'Productos / márgenes',
    type: 'report',
    route: '/ventas/reports/productos',
    locationPath: 'Ventas > Reportes > Productos / márgenes',
    generalDescription:
      'Ranking y detalle de productos vendidos con margen por ítem: útil para ajustar surtido, promociones y precios en Lista de precios. Combina cantidades vendidas, ingreso y costo estimado.',
    prerequisites: ['Ventas con productos y costos disponibles.'],
    interfaceElements: [
      {
        name: 'Filtros de período y POS',
        controlType: 'Fechas / select',
        required: true,
        editable: true,
        automatic: false,
        description: 'Define el universo del ranking.',
      },
      {
        name: 'Tabla ranking',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Producto, cantidad, venta, costo, margen %.',
      },
    ],
    procedure: [
      'Ventas → Reportes → Productos / márgenes.',
      'Seleccione fechas y POS.',
      'Ordene por margen o por cantidad vendida.',
      'Tome decisiones de precio en Lista de precios.',
    ],
    practicalExample: {
      context: 'Producto estrella con margen erosionado.',
      data: { Producto: 'Snack combo', Margen: '8 %' },
      steps: ['Identifique en ranking', 'Suba precio o negocie costo con compras'],
    },
    validations: [],
    expectedResult: ['Lista priorizada de productos para revisión comercial.'],
    commonErrors: [
      {
        error: 'Producto no aparece',
        cause: 'Solo servicios en el período o POS distinto.',
        solution: 'Amplíe filtros o use reporte de registro.',
      },
    ],
    relatedFunctions: ['ventas-report-margenes', 'ventas-lista-precios'],
    observations: [],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.ventas,
  }),

  functionDoc({
    id: 'ventas-report-liquidaciones',
    name: 'Liquidaciones de caja (reporte)',
    type: 'report',
    route: '/ventas/reports/liquidaciones-caja',
    locationPath: 'Ventas > Reportes > Liquidaciones de caja',
    generalDescription:
      'Historial de cierres de caja por POS: montos sistema vs declarados, diferencias y usuario que cerró. Complementa la pantalla operativa de Liquidación de caja para auditoría y gerencia.',
    prerequisites: ['Flag ventasCashLiquidationEnabled y cierres realizados.'],
    interfaceElements: [
      {
        name: 'Filtros POS y fechas',
        controlType: 'Select / fechas',
        required: true,
        editable: true,
        automatic: false,
        description: 'Acota liquidaciones históricas.',
      },
      {
        name: 'Tabla de liquidaciones',
        controlType: 'Tabla',
        required: false,
        editable: false,
        automatic: true,
        description: 'Fecha, POS, totales, diferencia, estado.',
      },
    ],
    procedure: [
      'Ventas → Reportes → Liquidaciones de caja.',
      'Filtre por POS y rango de fechas.',
      'Revise diferencias recurrentes.',
      'Cruce con Liquidación de caja del día si hubo reclamos.',
    ],
    practicalExample: {
      context: 'Auditoría de faltantes del trimestre.',
      data: { POS: 'Tienda 1', Trimestre: 'Q3 2026' },
      steps: ['Filtre jul–sep', 'Exporte si está disponible'],
    },
    validations: [],
    expectedResult: ['Historial completo de cierres en el filtro.'],
    commonErrors: [
      {
        error: 'No veo el reporte en menú',
        cause: 'Módulo de liquidación de caja desactivado.',
        solution: 'Active ventasCashLiquidationEnabled.',
      },
    ],
    relatedFunctions: ['ventas-liquidacion-caja'],
    observations: [],
    screenshots: [SHOTS.listado],
    roles: ROLES.ventas,
    isVisible: (ctx) => ctx.settings.ventasCashLiquidationEnabled,
  }),
];
